import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getLlmReferralSummary, type LlmReferralSummary } from '../services/llmReferrals';

/**
 * How many people AI assistants actually sent us, and what they landed on.
 *
 * The site was made legible to language models with no way to tell whether that
 * worked; this is the half that tells you. It answers the only question that
 * justifies the work — did a person arrive — rather than whether a model read
 * the page.
 */
export function LlmReferralsCard({ days }: { days: number }) {
  const [data, setData] = useState<LlmReferralSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getLlmReferralSummary(days)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load referrals.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>AI assistant referrals</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        People who arrived from ChatGPT, Perplexity, Claude, Gemini and the rest, matched on the
        referring site. Crawlers are deliberately excluded — a model reading the page is not a
        customer. Days are counted in UTC, so the boundary falls at 05:30 IST.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : !data ? null : data.total === 0 ? (
        <EmptyState lastRecordedAt={data.lastRecordedAt} days={days} />
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h2" sx={{ fontSize: '1.5rem' }}>{data.total}</Typography>
            <Typography variant="caption" color="text.secondary">
              {data.total === 1 ? 'visit' : 'visits'} in the last {days} days
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography variant="caption" color="text.secondary">Which assistant</Typography>
              <Table size="small">
                <TableBody>
                  {data.bySource.map((row) => (
                    <TableRow key={row.source}>
                      <TableCell>{row.source}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Box sx={{ width: `${Math.max(row.sharePercent, 2)}%`, maxWidth: 40, height: 6, bgcolor: 'secondary.main', borderRadius: 3 }} />
                          <span>{row.sharePercent}%</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Where they landed{data.pathsTruncated ? ' (most recent 1,000 visits)' : ''}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell align="right">Visits</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topPaths.map((row) => (
                    <TableRow key={row.path}>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{row.path}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                  {data.topPaths.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Counted, but the individual visits have aged out of the window.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </>
      )}
    </Paper>
  );
}

/**
 * A zero here has two very different meanings and the card must say which.
 *
 * This beacon has already failed silently once — it returned 204 and wrote
 * nothing for a full day, which looked exactly like a quiet week. So an empty
 * card reports when anything was last recorded rather than just showing a nought
 * and letting you assume the plumbing is fine.
 */
function EmptyState({ lastRecordedAt, days }: { lastRecordedAt: number | null; days: number }) {
  if (lastRecordedAt == null) {
    return (
      <Alert severity="info">
        Nothing recorded yet, ever. Expected until an assistant starts citing the site — but if it
        stays this way for weeks, check that /api/llm-visit is still writing rather than assuming
        the silence is real.
      </Alert>
    );
  }

  const when = new Date(lastRecordedAt);
  const daysAgo = Math.floor((Date.now() - lastRecordedAt) / (24 * 60 * 60 * 1000));

  return (
    <Alert severity="info">
      No referrals in the last {days} days. The counter was last written{' '}
      {daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`} (
      {when.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}), so
      the beacon is recording — this window is genuinely quiet.
    </Alert>
  );
}
